import { formatDate } from '@amelio/ui-library';

import { LevelsPath } from './LevelsPath';
import { SummarySection } from './SummarySection';

import type { LevelsPathStep } from './LevelsPath';

import { GROWTH_PANEL_SURFACE } from '@/components/CareerGrowth/careerGrowthStyles';
import { useAmelioTranslations } from '@/hooks';
import { useCompanyInformation } from '@/hooks/company/useCompanyInformation';
import { usePortalEvolution } from '@/pages/EmployeePortal/hooks/usePortalReads';
import { parseServerInstant } from '@/utils/dateUtils';

/**
 * "Vos niveaux s'afficheront après votre rencontre": what the summary shows instead of the two
 * competency sections when the employee's assessment is submitted but its levels wait for the
 * meeting, and no earlier cycle left them anything to read.
 *
 * It answers the one question the empty sections could not: not "is there anything about me?" but
 * "when do I get to see it?" -- so it draws the path rather than an absence.
 */
export const WithheldLevelsSection = () => {
    const { t } = useAmelioTranslations();
    const { getManagerLabel } = useCompanyInformation();
    const evolution = usePortalEvolution();
    const managerLabel = getManagerLabel();

    return (
        <SummarySection
            icon={'business-trend-up'}
            title={t('employeePortalSummary.withheldLevels.title')}
            subtitle={t('employeePortalSummary.withheldLevels.subtitle', { managerLabel })}
            evolution={evolution}
            dataTestId={'employee-portal-withheld-levels'}
        >
            {({ withheldEvaluation }) => (
                <WithheldLevelsSteps
                    evaluationDate={withheldEvaluation?.evaluationDate ?? null}
                    managerLabel={managerLabel}
                />
            )}
        </SummarySection>
    );
};

/**
 * The one line that says the same wait when the employee does have levels to read, from an earlier
 * cycle: those levels are the useful content of the summary, and the three steps drawn over them
 * would bury it. Said above the sections, because it is what explains how old they are.
 */
export const WithheldLevelsNotice = () => {
    const { t } = useAmelioTranslations();
    const { getManagerLabel } = useCompanyInformation();

    return (
        <p
            className={`${GROWTH_PANEL_SURFACE} text-growth-slate px-6 py-6 text-[14px]`}
            data-testid={'employee-portal-withheld-levels-notice'}
        >
            {t('employeePortalSummary.withheldLevels.notice', {
                managerLabel: getManagerLabel(),
            })}
        </p>
    );
};

interface WithheldLevelsStepsProps {
    /** The date the assessment carries; no date is written at all when it carries none. */
    evaluationDate: string | null;
    managerLabel: string;
}

/**
 * The three stops between a submitted assessment and the levels it holds. The first is behind the
 * employee, the second is where they stand, the third is what they are waiting for -- the stepper
 * draws each one from that status alone.
 */
const WithheldLevelsSteps = ({ evaluationDate, managerLabel }: WithheldLevelsStepsProps) => {
    const { t, i18n } = useAmelioTranslations();
    const evaluationDay = evaluationDate ? parseServerInstant(evaluationDate) : undefined;

    const steps: LevelsPathStep[] = [
        {
            id: 'submitted',
            label: t('employeePortalSummary.withheldLevels.steps.submitted'),
            description: evaluationDay
                ? formatDate({ date: evaluationDay, language: i18n.language })
                : undefined,
            status: 'completed',
        },
        {
            id: 'meeting',
            label: t('employeePortalSummary.withheldLevels.steps.meeting', { managerLabel }),
            status: 'active',
            iconName: 'electronics-clock',
        },
        {
            id: 'levels',
            label: t('employeePortalSummary.withheldLevels.steps.levels'),
            description: t('employeePortalSummary.withheldLevels.steps.levelsTiming'),
            status: 'upcoming',
            iconName: 'security-lock',
        },
    ];

    return (
        <LevelsPath
            steps={steps}
            ariaLabel={t('employeePortalSummary.withheldLevels.steps.ariaLabel')}
            dataTestId={'employee-portal-withheld-levels-steps'}
        />
    );
};
